import { dateLib, planLib, tenantLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import type { PlanRes, TenantRes } from "@agentcall/core/res"
import type { TenantOccupantResponse } from "@agentcall/core/response"
import { planMasterType } from "@agentcall/core/type"
import { type ReactNode, useState } from "react"
import { ArrowRight, Check, Edit2, MessageCircle, RefreshCw, User } from "react-feather"
import { Link, useNavigate } from "react-router"
import { ticketApi } from "@/api/client/ticket"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent, ChatListCard, EmptyCard } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog/dialog"
import { cn } from "@/lib/utils"

interface InfoItem {
  label: ReactNode
  value: ReactNode
  className?: string
  valueClassName?: string
}

interface RoomTenantInfoCardProps {
  tenant?: TenantRes | null
  nextTenant?: TenantRes | null
  onEdit?: () => void
  onNextEdit?: () => void
  onAdd?: () => void
  onCancel?: () => void
  onRevival?: () => void
  onChangeEndDate?: () => void
  onDelete?: () => void
  isReviving?: boolean
  isDeleting?: boolean
  isRevivalDialogOpen?: boolean
  setIsRevivalDialogOpen?: (open: boolean) => void
  isDeleteDialogOpen?: boolean
  setIsDeleteDialogOpen?: (open: boolean) => void
  planContent?: ReactNode
  nextPlanContent?: ReactNode
}

const formatOptionalPhone = (phone1?: string, phone2?: string, phone3?: string) => {
  if (!phone1 && !phone2 && !phone3) {
    return "-"
  }

  return formatPhone(phone1 ?? "", phone2 ?? "", phone3 ?? "")
}

export function RoomTenantInfoCard({
  tenant,
  nextTenant,
  onEdit,
  onNextEdit,
  onAdd,
  onCancel,
  onRevival,
  onChangeEndDate,
  onDelete,
  isReviving = false,
  isDeleting = false,
  isRevivalDialogOpen: externalIsRevivalDialogOpen,
  setIsRevivalDialogOpen: externalSetIsRevivalDialogOpen,
  isDeleteDialogOpen: externalIsDeleteDialogOpen,
  setIsDeleteDialogOpen: externalSetIsDeleteDialogOpen,
  planContent,
  nextPlanContent,
}: RoomTenantInfoCardProps) {
  const [internalIsRevivalDialogOpen, setInternalIsRevivalDialogOpen] = useState(false)
  const [internalIsDeleteDialogOpen, setInternalIsDeleteDialogOpen] = useState(false)

  const isRevivalDialogOpen = externalIsRevivalDialogOpen ?? internalIsRevivalDialogOpen
  const setIsRevivalDialogOpen = externalSetIsRevivalDialogOpen ?? setInternalIsRevivalDialogOpen

  const isDeleteDialogOpen = externalIsDeleteDialogOpen ?? internalIsDeleteDialogOpen
  const setIsDeleteDialogOpen = externalSetIsDeleteDialogOpen ?? setInternalIsDeleteDialogOpen

  const hasTenant = !!tenant
  const hasNextTenant = !!nextTenant
  const isLoading = isReviving || isDeleting

  const handleRevivalConfirm = () => {
    onRevival?.()
  }

  const handleDeleteConfirm = () => {
    onDelete?.()
  }

  return (
    <>
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex items-center justify-between">
            <SectionTitle icon={<User className="h-4 w-4 text-slate-400" />} title="現在の賃借人" />
            {hasTenant && onEdit && (
              <EchoesEditGuard>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={onEdit}>
                  <Edit2 className="h-3.5 w-3.5" />
                  編集
                </Button>
              </EchoesEditGuard>
            )}
          </div>

          <TenantInfoGrid tenant={tenant} />

          {hasTenant && tenant?.occupants && (
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <span className="text-sm font-semibold text-slate-600">入居者</span>
              {tenant.occupants.length === 0 ? (
                <p className="text-sm text-slate-400">入居者の登録はありません</p>
              ) : (
                <ul className="space-y-2">
                  {tenant.occupants.map((occupant: TenantOccupantResponse) => (
                    <li
                      key={occupant.id}
                      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm"
                    >
                      <span className="font-semibold text-slate-600">{occupant.name}</span>
                      {occupant.nameKana && (
                        <span className="text-slate-400">{occupant.nameKana}</span>
                      )}
                      {occupant.phone && (
                        <span className="text-slate-400">電話番号: {occupant.phone}</span>
                      )}
                      {occupant.birthday && (
                        <span className="text-slate-400">{occupant.birthday}</span>
                      )}
                      {occupant.note && (
                        <span className="basis-full text-slate-400">備考: {occupant.note}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {hasTenant && tenant && (
            <>
              {tenantLib.canRevival(tenant) && (
                <EchoesEditGuard block>
                  {hasNextTenant && onChangeEndDate ? (
                    <Button block onClick={onChangeEndDate} disabled={isLoading}>
                      退去日を変更する
                    </Button>
                  ) : (
                    <Button
                      block
                      onClick={() => setIsRevivalDialogOpen(true)}
                      disabled={isLoading}
                      loading={isReviving}
                    >
                      退去を取り消す
                    </Button>
                  )}
                </EchoesEditGuard>
              )}
              {tenantLib.canCancel(tenant) && (
                <EchoesEditGuard block>
                  <Button block onClick={onCancel} disabled={isLoading}>
                    退去手続きする
                  </Button>
                </EchoesEditGuard>
              )}
              {/* {tenantLib.canDelete(tenant) && (
                <Button
                  block
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isLoading}
                  loading={isDeleting}
                >
                  削除する
                </Button>
              )} */}
            </>
          )}
          {!hasTenant && !hasNextTenant && onAdd && (
            <EchoesEditGuard block>
              <Button block onClick={onAdd}>
                賃借人情報を追加する
              </Button>
            </EchoesEditGuard>
          )}
          {planContent}
        </CardContent>
      </Card>

      {(hasTenant || hasNextTenant) && onAdd && (
        <Card>
          <CardContent className="space-y-4 p-4">
            <SectionTitle icon={<User className="h-4 w-4 text-slate-400" />} title="次の賃借人" />
            {hasNextTenant && <TenantInfoGrid tenant={nextTenant} />}
            <Button block onClick={hasNextTenant ? onNextEdit : onAdd}>
              {hasNextTenant ? "次の賃借人情報を変更する" : "次の賃借人を追加する"}
            </Button>
            {nextPlanContent}
          </CardContent>
        </Card>
      )}

      <Dialog open={isRevivalDialogOpen} onOpenChange={setIsRevivalDialogOpen}>
        <DialogContent className="max-w-[322px] rounded-[10px] px-5 py-[30px]">
          <DialogTitle className="text-center text-lg font-semibold text-[#354267] tracking-[-0.135px]">
            退去を取り消しますか？
          </DialogTitle>
          <DialogDescription className="sr-only">
            退去の取り消しを確認するダイアログです
          </DialogDescription>
          <div className="flex flex-col gap-[15px] mt-[10px]">
            <Button
              variant="outline"
              className="w-full h-10 border-slate-500 text-slate-500 font-bold"
              onClick={() => setIsRevivalDialogOpen(false)}
              disabled={isReviving}
            >
              キャンセル
            </Button>
            <Button
              className="w-full h-10 font-bold"
              onClick={handleRevivalConfirm}
              disabled={isReviving}
              loading={isReviving}
            >
              退去を取り消す
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-[322px] rounded-[10px] px-5 py-[30px]">
          <DialogTitle className="text-center text-lg font-semibold text-[#354267] tracking-[-0.135px]">
            賃借人を削除しますか？
          </DialogTitle>
          <DialogDescription className="sr-only">
            賃借人の削除を確認するダイアログです
          </DialogDescription>
          <div className="flex flex-col gap-[15px] mt-[10px]">
            <Button
              variant="outline"
              className="w-full h-10 border-slate-500 text-slate-500 font-bold"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              キャンセル
            </Button>
            <Button
              variant="destructive"
              className="w-full h-10 font-bold"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              loading={isDeleting}
            >
              削除する
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

interface RoomPlanInfoCardProps {
  plan?: PlanRes | null
  /** 加入中のプランの後に始まる、開始前のプラン */
  nextPlan?: PlanRes | null
  onAdd?: () => void
  onCancel?: () => void
  onRevival?: () => void
  onChangeEndDate?: () => void
  onDelete?: (planId: number) => void
  isReviving?: boolean
  isDeleting?: boolean
  isRevivalDialogOpen?: boolean
  setIsRevivalDialogOpen?: (open: boolean) => void
  isDeleteDialogOpen?: boolean
  setIsDeleteDialogOpen?: (open: boolean) => void
  disableAdd?: boolean
  hasNextTenant?: boolean
}

function PlanDetail({ plan, label }: { plan: PlanRes; label: string }) {
  return (
    <div className="rounded-md border border-slate-300 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge>{label}</Badge>
          {plan.echoesPlanSuspended && <Badge variant="warning">サービス停止中</Badge>}
        </div>
        <p className="text-sm font-semibold text-slate-600">
          {planMasterType.isEchoesPlanMasterId(plan.planMasterId)
            ? "Echoes請求"
            : plan.monthlyPrice != null
              ? `${plan.monthlyPrice.toLocaleString()}円（税込）/ 月額`
              : "-"}
        </p>
      </div>
      <p className="font-semibold text-slate-700">{plan.planMaster?.name ?? "-"}</p>
      <ul className="space-y-1">
        {plan.planMaster?.features?.map((feature) => (
          <li key={feature} className="flex items-center gap-1 text-slate-600 font-semibold">
            <Check className="w-4 h-4 shrink-0" />
            <p className="text-xs">{feature}</p>
          </li>
        ))}
      </ul>
      <div className="flex gap-4">
        <p className="text-sm text-slate-400">開始日 {plan.startDate ?? "-"}</p>
        <p className="text-sm text-slate-400">終了日 {plan.endDate ?? "-"}</p>
      </div>
    </div>
  )
}

export function RoomPlanInfoCard({
  plan,
  nextPlan,
  onAdd,
  onCancel,
  onRevival,
  onChangeEndDate,
  onDelete,
  isReviving = false,
  isDeleting = false,
  isRevivalDialogOpen: externalIsRevivalDialogOpen,
  setIsRevivalDialogOpen: externalSetIsRevivalDialogOpen,
  isDeleteDialogOpen: externalIsDeleteDialogOpen,
  setIsDeleteDialogOpen: externalSetIsDeleteDialogOpen,
  disableAdd = false,
  hasNextTenant = false,
}: RoomPlanInfoCardProps) {
  const [internalIsRevivalDialogOpen, setInternalIsRevivalDialogOpen] = useState(false)
  const [internalIsDeleteDialogOpen, setInternalIsDeleteDialogOpen] = useState(false)

  const isRevivalDialogOpen = externalIsRevivalDialogOpen ?? internalIsRevivalDialogOpen
  const setIsRevivalDialogOpen = externalSetIsRevivalDialogOpen ?? setInternalIsRevivalDialogOpen

  const isDeleteDialogOpen = externalIsDeleteDialogOpen ?? internalIsDeleteDialogOpen
  const setIsDeleteDialogOpen = externalSetIsDeleteDialogOpen ?? setInternalIsDeleteDialogOpen

  // 加入中・次のプランのどちらを削除するか
  const [deleteTargetPlanId, setDeleteTargetPlanId] = useState<number | null>(null)

  const hasPlan = !!plan
  const isLoading = isReviving || isDeleting

  const handleRevivalConfirm = () => {
    onRevival?.()
  }

  const openDeleteDialog = (planId: number) => {
    setDeleteTargetPlanId(planId)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (deleteTargetPlanId !== null) {
      setIsDeleteDialogOpen(false)
      onDelete?.(deleteTargetPlanId)
    }
  }

  return (
    <>
      <Card>
        <CardContent className="space-y-4 p-4">
          <SectionTitle
            icon={<RefreshCw className="h-4 w-4 text-slate-400" />}
            title="プラン情報"
          />

          {hasPlan ? (
            <>
              <PlanDetail
                plan={plan}
                label={planLib.isFuture(plan, null) ? "開始前のプラン" : "加入中のプラン"}
              />
              {plan && (
                <>
                  {planLib.canRevival(plan) &&
                    (hasNextTenant && onChangeEndDate ? (
                      <Button block onClick={onChangeEndDate} disabled={isLoading}>
                        終了日を変更する
                      </Button>
                    ) : (
                      <Button
                        block
                        onClick={() => setIsRevivalDialogOpen(true)}
                        disabled={isLoading}
                        loading={isReviving}
                      >
                        終了を取り消す
                      </Button>
                    ))}
                  {/* 開始前のプランは終了させず、削除して作り直す */}
                  {planLib.canCancel(plan) && !planLib.isFuture(plan, null) && (
                    <Button block onClick={onCancel} disabled={isLoading}>
                      プランを終了
                    </Button>
                  )}
                  {onDelete && planLib.canDelete(plan) && (
                    <Button
                      block
                      onClick={() => openDeleteDialog(plan.id)}
                      disabled={isLoading}
                      loading={isDeleting}
                    >
                      プランを削除
                    </Button>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <EmptyCard text="この部屋はプランに加入していません" />
              {onAdd && (
                <Button block onClick={onAdd} disabled={disableAdd}>
                  プランに加入する
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 次のプランが登録されている場合のみ表示する */}
      {nextPlan && (
        <Card>
          <CardContent className="space-y-4 p-4">
            <SectionTitle
              icon={<RefreshCw className="h-4 w-4 text-slate-400" />}
              title="次のプラン"
            />
            {nextPlan ? (
              <>
                <PlanDetail plan={nextPlan} label="開始前のプラン" />
                {onDelete && planLib.canDelete(nextPlan) && (
                  <Button
                    block
                    onClick={() => openDeleteDialog(nextPlan.id)}
                    disabled={isLoading}
                    loading={isDeleting}
                  >
                    プランを削除
                  </Button>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>
      )}

      <Dialog open={isRevivalDialogOpen} onOpenChange={setIsRevivalDialogOpen}>
        <DialogContent className="max-w-[322px] rounded-[10px] px-5 py-[30px]">
          <DialogTitle className="text-center text-lg font-semibold text-[#354267] tracking-[-0.135px]">
            プラン終了を取り消しますか？
          </DialogTitle>
          <DialogDescription className="sr-only">
            プラン終了の取り消しを確認するダイアログです
          </DialogDescription>
          <div className="flex flex-col gap-[15px] mt-[10px]">
            <Button
              variant="outline"
              className="w-full h-10 border-slate-500 text-slate-500 font-bold"
              onClick={() => setIsRevivalDialogOpen(false)}
              disabled={isReviving}
            >
              キャンセル
            </Button>
            <Button
              className="w-full h-10 font-bold"
              onClick={handleRevivalConfirm}
              disabled={isReviving}
              loading={isReviving}
            >
              終了を取り消す
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-[322px] rounded-[10px] px-5 py-[30px]">
          <DialogTitle className="text-center text-lg font-semibold text-[#354267] tracking-[-0.135px]">
            プランを削除しますか？
          </DialogTitle>
          <DialogDescription className="sr-only">
            プランの削除を確認するダイアログです
          </DialogDescription>
          <div className="flex flex-col gap-[15px] mt-[10px]">
            <Button
              variant="outline"
              className="w-full h-10 border-slate-500 text-slate-500 font-bold"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              キャンセル
            </Button>
            <Button
              variant="destructive"
              className="w-full h-10 font-bold"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              loading={isDeleting}
            >
              削除する
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const TICKET_DISPLAY_LIMIT = 3

interface RoomRelatedCasesSectionProps {
  propertyId: number
  roomId: number
  tenantId: number
}

export function RoomRelatedCasesSection({
  propertyId,
  roomId,
  tenantId,
}: RoomRelatedCasesSectionProps) {
  const navigate = useNavigate()
  const { data: ticketResponse } = ticketApi.useTicketListSuspense({
    propertyId,
    roomId,
    tenantId,
    limit: TICKET_DISPLAY_LIMIT,
  })

  const tickets = ticketResponse?.tickets ?? []

  return (
    <section className="space-y-2">
      <SectionTitle
        icon={<MessageCircle className="h-4 w-4 text-slate-400" />}
        title="関連案件（現在の賃借人）"
      />
      <div className="space-y-2">
        {tickets.length === 0 && <EmptyCard text="関連案件がありません" />}
        {tickets.map((ticket) => (
          <button
            key={ticket.id}
            type="button"
            onClick={() => navigate(`/tickets/${ticket.id}`)}
            className="w-full text-left"
          >
            <ChatListCard
              title={ticket.title}
              titleIcon={<MessageCircle className="h-4 w-4 text-slate-400" />}
              description={ticket.content}
              date={dateLib.formatDateTime(ticket.updatedAt)}
            />
          </button>
        ))}
      </div>
      <div className="text-right">
        <Link to={`/tickets?propertyId=${propertyId}&roomId=${roomId}&tenantId=${tenantId}`}>
          <Button variant={"ghost"} className="font-bold">
            一覧を開く
            <ArrowRight className="size-4" />
          </Button>
        </Link>
      </div>
    </section>
  )
}

interface SectionTitleProps {
  icon: ReactNode
  title: string
}

function SectionTitle({ icon, title }: SectionTitleProps) {
  return (
    <div className="flex items-center gap-2">
      {icon}
      <h2 className="text-base font-semibold text-[#354267]">{title}</h2>
    </div>
  )
}

function getTenantInfoItems(tenant?: TenantRes | null): InfoItem[] {
  return [
    { label: "氏名", value: tenant?.name ?? "-" },
    { label: "氏名（カナ）", value: tenant?.nameKana ?? "-" },
    {
      label: "電話番号1",
      value: tenant ? formatPhone(tenant.phone1, tenant.phone2, tenant.phone3) : "-",
    },
    {
      label: "電話番号2",
      value: tenant ? formatOptionalPhone(tenant.phone4, tenant.phone5, tenant.phone6) : "-",
    },
    { label: "メールアドレス", value: tenant?.email ?? "-", className: "col-span-2" },
    { label: "初回賃貸借契約開始日", value: tenant?.startDate ?? "-" },
    { label: "賃貸借契約終了日", value: tenant?.endDate ?? "-" },
  ]
}

function TenantInfoGrid({ tenant }: { tenant?: TenantRes | null }) {
  const items = getTenantInfoItems(tenant)

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
      {items.map((item, index) => {
        const key =
          typeof item.label === "string" ? `tenant-info-${item.label}` : `tenant-info-${index}`
        return <InfoItemText key={key} {...item} />
      })}
    </div>
  )
}

function InfoItemText({ label, value, className, valueClassName }: InfoItem) {
  return (
    <div className={cn("space-y-1", className)}>
      <p className="text-xs font-semibold text-slate-400">{label}</p>
      <div className={cn("text-sm text-slate-600 whitespace-normal break-words", valueClassName)}>
        {value}
      </div>
    </div>
  )
}
